import type { ReactElement, ReactNode } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { useAuth } from "./contexts/AuthContext";
import { Layout } from "./layouts/Layout";
import { AdminPage } from "./pages/AdminPage";
import { CatalogPage } from "./pages/CatalogPage";
import { DemoLauncherPage } from "./pages/DemoLauncherPage";
import { ProductDetailPage } from "./pages/ProductDetailPage";
import { HomePage } from "./pages/HomePage";
import { InvitePage } from "./pages/InvitePage";
import { LoginPage } from "./pages/LoginPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { AdminAuditLogPage } from "./pages/AdminAuditLogPage";
import { AdminCallsPage } from "./pages/AdminCallsPage";
import { AdminLeadsPage } from "./pages/AdminLeadsPage";
import { AdminLeadDetailPage } from "./pages/AdminLeadDetailPage";
import { AdminTenantsPage } from "./pages/AdminTenantsPage";
import { CompareProductsPage } from "./pages/CompareProductsPage";
import { HealthDashboardPage } from "./pages/HealthDashboardPage";
import { IndustryHubPage } from "./pages/IndustryHubPage";
import { PublicUptimePage } from "./pages/PublicUptimePage";
import { RequestDemoPage } from "./pages/RequestDemoPage";
import { SuiteLandingPage } from "./pages/SuiteLandingPage";
import { TenantLandingPage } from "./pages/TenantLandingPage";
import { useTenant } from "./contexts/TenantContext";

/**
 * Root application component с defined routes.
 *
 * Public routes (no auth): /, /login, /invite/:token
 * Protected client routes: /catalog, /demo/:slug (require session)
 * Admin routes: /admin (require admin JWT в localStorage)
 */
export function App(): ReactElement {
  const tenantCtx = useTenant();

  // Cycle 22 ADR-008 addendum (Tenant URL Routing — STORY-O2/O3):
  // If hostname is <slug>.it-navigator.online → show TenantLandingPage в root.
  // Otherwise — main portal routes (HomePage, etc.).
  if (tenantCtx.isSubdomainTenant) {
    return (
      <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<TenantLandingPage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="*" element={<TenantLandingPage />} />
        </Route>
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />

        {/* Cycle 22 STORY-O3 (ADR-008 addendum): path-based tenant landing */}
        <Route path="t/:slug" element={<TenantLandingPage />} />
        <Route path="t/:slug/login" element={<LoginPage />} />
        <Route path="t/:slug/*" element={<TenantLandingPage />} />
        <Route path="login" element={<LoginPage />} />
        <Route path="invite/:token" element={<InvitePage />} />

        {/* Cycle 8 STORY-702: public suite landing pages (no auth) */}
        <Route path="suite/:name" element={<SuiteLandingPage />} />

        {/* Cycle 8 STORY-704: public request-demo form (no auth) */}
        <Route path="request-demo" element={<RequestDemoPage />} />

        {/* Cycle 9 STORY-803: public industry hub pages (no auth) */}
        <Route path="industry/:name" element={<IndustryHubPage />} />

        {/* Cycle 9 STORY-802: public compare products page (no auth) */}
        <Route path="compare" element={<CompareProductsPage />} />

        {/* Cycle 15 STORY-1504: public uptime page (no auth) */}
        <Route path="uptime" element={<PublicUptimePage />} />

        {/* Cycle 15 STORY-1502: admin health dashboard */}
        <Route path="admin/health" element={<HealthDashboardPage />} />

        {/* Cycle 17 STORY-1703: admin leads management */}
        <Route path="admin/leads" element={<AdminLeadsPage />} />
        {/* ЦИКЛ B / B1: карточка лида (детальный просмотр + действия) */}
        <Route path="admin/leads/:id" element={<AdminLeadDetailPage />} />

        {/* Cycle 17 STORY-1704: admin calls management (Novofon click-to-call) */}
        <Route path="admin/calls" element={<AdminCallsPage />} />
        {/* Cycle 22 STORY-N2 (ADR-008 Phase 4): tenant management UI */}
        <Route path="admin/tenants" element={<AdminTenantsPage />} />
        {/* Cycle 22 STORY-CC3: admin audit log forensics */}
        <Route path="admin/audit-log" element={<AdminAuditLogPage />} />

        <Route
          path="catalog"
          element={
            <ProtectedRoute>
              <CatalogPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="product/:slug"
          element={
            <ProtectedRoute>
              <ProductDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="demo/:slug"
          element={
            <ProtectedRoute>
              <DemoLauncherPage />
            </ProtectedRoute>
          }
        />

        <Route path="admin/*" element={<AdminPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/** Guard: требует authenticated session. */
function ProtectedRoute({ children }: { children: ReactNode }): ReactElement {
  const { session, loading } = useAuth();

  if (loading) {
    return (
      <div className="route-loading">
        <p>Загрузка…</p>
      </div>
    );
  }

  if (!session?.authenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
